import { useTranslation } from 'react-i18next';
import React from 'react';
import { type Doctor } from './types';
import { useTheme } from '../../theme/ThemeContext';

export interface CalendarContextMenuState {
  x: number;
  y: number;
  type: 'appointment' | 'event';
  itemId: number;
}

interface CalendarContextMenuProps {
  menu: CalendarContextMenuState;
  doctors: Doctor[];
  hasWorkspaceShortcut: boolean;
  onClose: () => void;
  onDoctorChange: (doctorId: number | null) => void;
  onOpenTreatmentWorkspace: () => void;
  onDeleteAppointment: () => void;
}

const menuItemStyle: React.CSSProperties = {
  padding: '6px 8px',
  cursor: 'pointer',
  fontSize: 13,
  borderRadius: 4
};

export const CalendarContextMenu: React.FC<CalendarContextMenuProps> = ({
  menu,
  doctors,
  hasWorkspaceShortcut,
  onClose,
  onDoctorChange,
  onOpenTreatmentWorkspace,
  onDeleteAppointment
}) => {
  const { tokens } = useTheme();
  const { t } = useTranslation();

  return (
  <>
    <div style={{ position: 'fixed', inset: 0, zIndex: 9998 }} onClick={onClose} onContextMenu={event => { event.preventDefault(); onClose(); }} />
    <div className="calendar-context-menu" style={{ position: 'fixed', top: menu.y, left: menu.x, background: tokens.app.surface, border: `1px solid ${tokens.app.border}`, borderRadius: 6, padding: 4, zIndex: 9999, boxShadow: tokens.app.shadow, minWidth: 160 }}>
      <div style={{ padding: '4px 8px', fontSize: 12, color: tokens.app.muted, borderBottom: `1px solid ${tokens.app.border}`, marginBottom: 4 }}>
        {t('responsive.assignDoctor')}
      </div>
      {doctors.map(doctor => (
        <div
          key={doctor.id}
          onClick={() => onDoctorChange(doctor.id)}
          style={{ ...menuItemStyle, color: tokens.app.text, display: 'flex', alignItems: 'center', gap: 8 }}
          onMouseEnter={event => event.currentTarget.style.background = tokens.app.surfaceAlt}
          onMouseLeave={event => event.currentTarget.style.background = 'transparent'}
        >
          <div style={{ width: 12, height: 12, borderRadius: 2, background: doctor.color || '#667eea' }} />
          {doctor.firstName} {doctor.lastName}
        </div>
      ))}
      <div
        onClick={() => onDoctorChange(null)}
        style={{ ...menuItemStyle, color: tokens.app.muted, display: 'flex', alignItems: 'center', gap: 8 }}
        onMouseEnter={event => event.currentTarget.style.background = tokens.app.surfaceAlt}
        onMouseLeave={event => event.currentTarget.style.background = 'transparent'}
      >
        <div style={{ width: 12, height: 12, borderRadius: 2, border: `1px dashed ${tokens.app.muted}` }} />
        {t('responsive.noDoctor')}
      </div>
      {menu.type === 'appointment' && (
        <>
          <div style={{ borderTop: `1px solid ${tokens.app.border}`, margin: '4px 0' }} />
          {hasWorkspaceShortcut && (
            <div
              onClick={onOpenTreatmentWorkspace}
              style={{ ...menuItemStyle, color: '#38bdf8', fontWeight: 600 }}
              onMouseEnter={event => event.currentTarget.style.background = tokens.app.surfaceAlt}
              onMouseLeave={event => event.currentTarget.style.background = 'transparent'}
            >
              {t('responsive.openTreatment')}
            </div>
          )}
          <div
            onClick={onDeleteAppointment}
            style={{ ...menuItemStyle, color: '#f87171', fontWeight: 600 }}
            onMouseEnter={event => event.currentTarget.style.background = tokens.app.surfaceAlt}
            onMouseLeave={event => event.currentTarget.style.background = 'transparent'}
          >
            {t('responsive.deleteAppointment')}
          </div>
        </>
      )}
    </div>
  </>
  );
};
